import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Form, Loader, Label, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  useAudiobookBetaQuote,
  useRecordAudiobookBetaView,
  useRequestAudiobookBeta,
} from 'src/hooks/useAudiobookBeta';

/**
 * The audiobook beta-signup teaser (docs/features/audiobooks/beta-teaser.md §2.2), as an
 * INLINE block — the "no access" state of the author Audiobook panel
 * (containers/book/bookAudiobook.tsx). It is explicit that the beta is PRIVATE and
 * PAID, shows the estimated per-book production cost, and captures paid-INTENT via
 * a waitlist request. No money is taken — intent only.
 */

const Hero = styled.div`
  height: 96px;
  background: linear-gradient(135deg, #6a5cf0, #8a3ffc);
  border-radius: 14px 14px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroIcon = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 28px;
`;

const Body = styled.div`
  padding: 20px 22px 22px;
  border: 1px solid #e6e1fb;
  border-top: none;
  border-radius: 0 0 14px 14px;
`;

const PriceCard = styled.div`
  margin: 14px 0;
  padding: 16px;
  border: 1px solid #e6e1fb;
  background: var(--br-raised-surface);
  border-radius: 12px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const PriceUsd = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: #3b2fae;
`;

const PriceSub = styled.span`
  font-size: 13px;
  color: var(--br-muted);
`;

const Perk = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
`;

type Props = {
  bookId?: string;
  /** Fallback title while the quote loads. */
  bookTitle?: string;
  /**
   * QA preview: rendered for a user who actually HAS access (forced via
   * `?audiobookTeaser=1`). Suppresses the impression event so a preview doesn't
   * pollute the A/B conversion denominator.
   */
  preview?: boolean;
};

const AudiobookBetaSignup: React.FC<Props> = ({
  bookId,
  bookTitle,
  preview = false,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = React.useState('');

  const quoteQuery = useAudiobookBetaQuote(bookId, { enabled: Boolean(bookId) });
  const recordView = useRecordAudiobookBetaView();
  const requestBeta = useRequestAudiobookBeta(bookId);

  const quote = quoteQuery.data;
  const title = quote?.title || bookTitle || t('YourBook', 'your book');

  // Fire the impression once on mount (the A/B denominator). Best-effort. Skipped
  // for a QA preview so it doesn't count as a real teaser impression.
  const viewMutate = recordView.mutate;
  useEffect(() => {
    if (bookId && !preview) {
      viewMutate(bookId);
    }
    // Intentionally keyed on bookId+preview only: one impression per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, preview]);

  const submitted = requestBeta.isSuccess || Boolean(quote?.alreadyRequested);

  const usdLabel = quote != null ? `$${quote.usd.toLocaleString()}` : '—';
  const creditsLabel =
    quote != null
      ? t('AudiobookBetaCredits', '{{credits}} credits', {
          credits: quote.credits.toLocaleString(),
        })
      : '';
  const wordsLabel =
    quote != null
      ? t('AudiobookBetaWords', '~{{words}} words', {
          words: quote.wordCount.toLocaleString(),
        })
      : '';

  return (
    <div data-testid="audiobook-beta-signup">
      <Hero>
        <HeroIcon>
          <Icon name="headphones" style={{ margin: 0 }} />
        </HeroIcon>
      </Hero>
      <Body>
        {submitted ? (
          <div data-testid="audiobook-beta-submitted">
            <h3 style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>
              {t('AudiobookBetaThanksTitle', "You’re on the list")}
            </h3>
            <p
              style={{
                margin: '12px 0',
                color: 'var(--br-muted)',
                fontSize: 14.5,
                lineHeight: 1.55,
              }}
            >
              {t(
                'AudiobookBetaThanksBody',
                'Thanks for your interest in the audiobook beta for “{{title}}”. It’s a private, paid beta — we’ll email you when a spot opens up.',
                { title }
              )}
            </p>
          </div>
        ) : (
          <>
            <h3 style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>
              {t('AudiobookBetaTitle', 'Turn “{{title}}” into an audiobook', {
                title,
              })}
            </h3>
            <p
              style={{
                margin: '10px 0 0',
                color: 'var(--br-muted)',
                fontSize: 14.5,
                lineHeight: 1.55,
              }}
            >
              {t(
                'AudiobookBetaBody',
                'We’re opening a private, paid beta for AI-narrated audiobooks — a natural-sounding voice reading your manuscript, in English or Swedish. Request access and we’ll be in touch.'
              )}
            </p>

            <PriceCard data-testid="audiobook-beta-price">
              {quoteQuery.isLoading ? (
                <Loader active inline size="small" />
              ) : quoteQuery.isError ? (
                <PriceSub>
                  {t(
                    'AudiobookBetaPriceError',
                    'We’ll estimate your price after you join.'
                  )}
                </PriceSub>
              ) : (
                <>
                  <PriceSub>
                    {t(
                      'AudiobookBetaPriceLabel',
                      'Estimated production cost for this book'
                    )}
                  </PriceSub>
                  <PriceRow>
                    <PriceUsd>{usdLabel}</PriceUsd>
                    <PriceSub>
                      {creditsLabel} · {wordsLabel}
                    </PriceSub>
                  </PriceRow>
                  <div style={{ marginTop: 6 }}>
                    <Label size="tiny" color="violet" basic>
                      {t('AudiobookBetaPricingTag', 'Beta pricing · estimated')}
                    </Label>
                  </div>
                </>
              )}
            </PriceCard>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
                margin: '4px 0 14px',
              }}
            >
              <Perk>
                <Icon name="check" style={{ color: '#2fa84f' }} />
                {t('AudiobookBetaPerkVoice', 'Natural AI narration of your book')}
              </Perk>
              <Perk>
                <Icon name="check" style={{ color: '#2fa84f' }} />
                {t(
                  'AudiobookBetaPerkListen',
                  'Let your beta readers listen and give feedback'
                )}
              </Perk>
              <Perk>
                <Icon name="check" style={{ color: '#2fa84f' }} />
                {t('AudiobookBetaPerkPay', 'Pay only if you decide to go ahead')}
              </Perk>
            </div>

            <Form>
              <Form.TextArea
                data-testid="audiobook-beta-message"
                rows={2}
                value={message}
                onChange={(_e, { value }) => setMessage(String(value ?? ''))}
                placeholder={t(
                  'AudiobookBetaMessagePlaceholder',
                  'Anything you’d like us to know? (optional)'
                )}
              />
            </Form>

            {requestBeta.isError && (
              <Message negative size="tiny" style={{ marginTop: 12 }}>
                {t(
                  'AudiobookBetaRequestError',
                  'Something went wrong. Please try again.'
                )}
              </Message>
            )}

            <div style={{ marginTop: 16 }}>
              <Button
                color="violet"
                onClick={() => requestBeta.mutate({ message })}
                loading={requestBeta.isLoading}
                disabled={requestBeta.isLoading}
                style={{ borderRadius: 10, fontWeight: 700 }}
                data-testid="audiobook-beta-request"
              >
                {t('AudiobookBetaRequest', 'Request beta access')}
              </Button>
            </div>
          </>
        )}
      </Body>
    </div>
  );
};

export default AudiobookBetaSignup;
